import { FC, useMemo, useState } from "react"
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { DeleteOutlined } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { DataGrid } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { useSnackbar } from "notistack"
import { UserbotBatches } from "../interfaces"
import {
  useCreateProfileDictionaryMutation,
  useDeleteProfileDictionaryMutation,
  useProfileDictionariesQuery,
} from "../store"
import { UserbotPage } from "./userbotPage"
import { PROFILE_DICT_KIND_LABELS } from "../constants/dictionaryKinds"
import { TooltipAction } from "../components/common/tooltipAction"
import { ConfirmDialog } from "../components/common/confirmDialog"
import { ImportDictionariesModal } from "../components/dictionaries/importDictionariesModal"

export const ProfileDictionariesPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [kind, setKind] = useState<UserbotBatches.ProfileDictKind | "all">(
    "all",
  )
  const [newValue, setNewValue] = useState("")
  const [newKind, setNewKind] = useState<UserbotBatches.ProfileDictKind>(
    UserbotBatches.PROFILE_DICT_KINDS[0],
  )
  const [addError, setAddError] = useState<string | null>(null)
  const [deleting, setDeleting] =
    useState<UserbotBatches.ProfileDictItem | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(500)

  const { data, isFetching, isError, error } = useProfileDictionariesQuery(
    kind === "all"
      ? { limit: pageSize, offset: page * pageSize }
      : { kind, limit: pageSize, offset: page * pageSize },
  )
  const [createDict, { isLoading: isCreating }] =
    useCreateProfileDictionaryMutation()
  const [deleteDict, { isLoading: isDeleting }] =
    useDeleteProfileDictionaryMutation()

  const columns: GridColDef<UserbotBatches.ProfileDictItem>[] = useMemo(
    () => [
      {
        field: "value",
        headerName: "Значение",
        flex: 2,
      },
      {
        field: "kind",
        headerName: "Тип",
        flex: 1.5,
        valueGetter: ({ row }) => PROFILE_DICT_KIND_LABELS[row.kind],
      },
      {
        field: "updatedAt",
        headerName: "Обновлён",
        flex: 1.5,
        valueGetter: ({ row }) => formatDateAndTime(row.updatedAt),
      },
    ],
    [],
  )

  const handleAdd = async () => {
    const value = newValue.trim()
    if (!value) return
    setAddError(null)
    try {
      await createDict({ body: { kind: newKind, value } }).unwrap()
      setNewValue("")
    } catch (e) {
      setAddError(
        (e as { status?: number }).status === 409
          ? "Такое значение уже есть"
          : getApiErrorMessage(e),
      )
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteDict({ id: deleting.id }).unwrap()
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
    } finally {
      setDeleting(null)
    }
  }

  return (
    <UserbotPage title="Словари профилей">
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Тип словаря"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as UserbotBatches.ProfileDictKind | "all")
            setPaginationModel({ page: 0, pageSize })
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">Все типы</MenuItem>
          {UserbotBatches.PROFILE_DICT_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {PROFILE_DICT_KIND_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Новое значение"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          error={!!addError}
          helperText={addError}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Тип нового значения"
          value={newKind}
          onChange={(e) =>
            setNewKind(e.target.value as UserbotBatches.ProfileDictKind)
          }
          sx={{ minWidth: 220 }}
        >
          {UserbotBatches.PROFILE_DICT_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {PROFILE_DICT_KIND_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          disabled={isCreating || !newValue.trim()}
          onClick={handleAdd}
        >
          Добавить
        </Button>
        <Button variant="outlined" onClick={() => setImportOpen(true)}>
          Импорт списком
        </Button>
      </Stack>

      {isError && (
        <Typography color="error" mb={1}>
          {getApiErrorMessage(error)}
        </Typography>
      )}

      <Box height="65vh">
        <DataGrid<UserbotBatches.ProfileDictItem>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          rowActions={[
            (r) => (
              <TooltipAction
                label="Удалить"
                icon={<DeleteOutlined />}
                onClick={() => setDeleting(r.row)}
              />
            ),
          ]}
        />
      </Box>

      {importOpen && (
        <ImportDictionariesModal onClose={() => setImportOpen(false)} />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Удалить значение?"
        description={deleting?.value}
        confirmText="Удалить значение"
        loading={isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </UserbotPage>
  )
}
